/**
 * Anti-corruption layer for the community site map API response.
 *
 * @module features/nativeInteractiveSiteMap/services/processCommunityMap
 */

import LotStatus from '../enums/lotStatus';

function resolveLotStatus(rawStatus) {
  const normalized = rawStatus?.toLowerCase() ?? '';
  return (LotStatus.fromKey(normalized) ?? LotStatus.Unavailable).toString();
}

/**
 * Maps raw site plan lots to the internal lot shape.
 *
 * @param {object[]} sitePlanLots - Raw PascalCase lot entries
 * @returns {object[]}
 */
function mapLots(sitePlanLots) {
  return (sitePlanLots ?? []).map((lot) => ({
    lotId: lot.SitePlanLotId,
    status: resolveLotStatus(lot.LotStatusType),
    housePlanIds: lot.HousePlanIds ?? [],
    housingUnitId: lot.HousingUnitId ?? null,
  }));
}

/**
 * Maps a raw API site plan to the internal SiteMapResource shape.
 *
 * @param {object} siteMapApiResponse - Raw site plan entry from the API response (PascalCase)
 * @returns {SiteMapResource}
*/
function mapSitePlanToResource(siteMapApiResponse) {
  return {
    mapBlobUrl:          siteMapApiResponse.MapBlobUrl,
    bottomLeftLatitude:  siteMapApiResponse.BottomLeftLatitude,
    bottomLeftLongitude: siteMapApiResponse.BottomLeftLongitude,
    topRightLatitude:    siteMapApiResponse.TopRightLatitude,
    topRightLongitude:   siteMapApiResponse.TopRightLongitude,
    lots:                (siteMapApiResponse.SitePlans ?? []).flatMap((lots) => mapLots(lots.SitePlanLots)),
  };
}

/**
 * Maps the community site map API response to the internal SiteMapResource shape.
 * Returns null for empty/falsy input so the epic's filter(Boolean) discards it.
 *
 * @param {object} siteMapApiResponse - Raw PascalCase API response
 * @returns {SiteMapResource|null}
 */
function processCommunityMap(siteMapApiResponse) {
  if (!siteMapApiResponse) {
    return null;
  }

  return mapSitePlanToResource(siteMapApiResponse);
}

export default processCommunityMap;
